// src/features/admin/migrationParser.js
//
// Pure parsing + validation for the MigrationPanel "Validate" button.
// Kept as a separate module so it can be unit-tested without mounting
// the React component or hitting the network.
//
// Two entry points:
//   parseLegacyJson(text)  : JSON.parse + array shape check
//   validateForImport(arr) : runs every record through normalizeForSchema
//                            and accumulates a count + issue report
//
// We deliberately accept the RAW localStorage shape (camelCase keys with
// `imageUrl`, `locationAlias`, etc.) — that's what Corey's exported JSON
// backup looks like. normalizeForSchema is the single seam that maps it
// to the snake_case payload /api/bulk-import expects.

import { normalizeForSchema } from "../../services/persistence.js";

const LOCATION_ID_RE = /^(AMER|EMEA|APAC)-[A-Z]{3}-\d{2}$/;
const VALID_REGION   = new Set(["AMER", "EMEA", "APAC"]);
// LSE-VACATED: keep in lockstep with locationsClient.VALID_STAGE and the
// server STAGES Set. Admin bulk-import rows with stage='vacated' otherwise
// silently fail validation. Order mirrors the SSOT (Active → Pipeline →
// Inactive); Set membership is order-agnostic but the visual symmetry aids
// future audits. (L52)
// OFFICE-STAGE-REMOVE-NEGOTIATING-PROSPECTING: negotiating + prospecting removed.
const VALID_STAGE    = new Set(["draft", "build", "operational", "dormant", "vacated", "closed"]);
const VALID_HEALTH   = new Set(["green", "yellow", "red"]);

/**
 * Parse the raw text from the file picker / textarea. Throws on
 * malformed JSON or a non-array top level.
 *
 * @param {string} text
 * @returns {Array<object>}
 */
export function parseLegacyJson(text) {
  if (typeof text !== "string" || text.trim() === "") {
    throw new Error("Empty input");
  }
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    throw new Error(`Invalid JSON: ${err.message}`);
  }
  // Accept either a bare array (the common case from a localStorage dump)
  // or a wrapper object with a `locations` field (some older exports
  // wrapped the payload to carry fxRates and deleted ids alongside).
  if (Array.isArray(parsed)) return parsed;
  if (parsed && Array.isArray(parsed.locations)) return parsed.locations;
  throw new Error("JSON must be an array of locations (or { locations: [...] }).");
}

/**
 * Validate + count + normalize. Never throws — every issue ends up in
 * the returned `issues` list so the UI can render the full picture and
 * the admin can decide whether to proceed.
 *
 * @param {Array<object>} rawLocations  As returned by parseLegacyJson.
 * @returns {{
 *   ok: boolean,
 *   issues: Array<{ id: string|null, severity: 'error'|'warning', message: string }>,
 *   normalized: Array<object>,        // ready to POST to /api/bulk-import
 *   counts: object,                   // per-entity counts
 *   locationIds: string[]
 * }}
 */
export function validateForImport(rawLocations) {
  const issues = [];
  const counts = {
    locations:    0,
    leases:       0,
    allowances:   0,
    draws:        0,
    criticalDates: 0,
    watchItems:   0,
    projects:     0,
    candidates:   0,
    commentary:   0,
    utilization:  0,
    headcount:    0,
    locationCosts: 0,
    photosInline:  0, // count of records that still carry a base64 imageUrl
    leasesSkipped: 0, // stub leases dropped pre-import (both startDate and endDate empty)
  };
  const seenIds = new Set();
  const locationIds = [];
  const normalized = [];

  if (!Array.isArray(rawLocations)) {
    return {
      ok: false,
      issues: [{ id: null, severity: "error", message: "Top-level value is not an array." }],
      normalized: [],
      counts,
      locationIds: [],
    };
  }

  for (let i = 0; i < rawLocations.length; i++) {
    const raw = rawLocations[i];
    const at = `index ${i}`;

    if (!raw || typeof raw !== "object") {
      issues.push({ id: null, severity: "error", message: `${at}: not an object` });
      continue;
    }

    // ID gate — without a matching id we can't do anything else with the row.
    const id = raw.id;
    if (!id || typeof id !== "string") {
      issues.push({ id: null, severity: "error", message: `${at}: missing or non-string id` });
      continue;
    }
    if (!LOCATION_ID_RE.test(id)) {
      issues.push({ id, severity: "error", message: `${at}: id "${id}" does not match REGION-CITYCODE-SEQ (e.g. AMER-SFO-01)` });
      continue;
    }
    if (seenIds.has(id)) {
      issues.push({ id, severity: "error", message: `${at}: duplicate id "${id}" within payload` });
      continue;
    }
    seenIds.add(id);

    // Required-enum gates. Same rules as the server CHECK constraints
    // and the validators in src/data/locationsClient.js#assertValidId.
    if (raw.region && !VALID_REGION.has(raw.region)) {
      issues.push({ id, severity: "error", message: `region "${raw.region}" not in {AMER, EMEA, APAC}` });
    }
    if (!raw.stage || !VALID_STAGE.has(raw.stage)) {
      issues.push({ id, severity: "error", message: `stage "${raw.stage ?? "<missing>"}" not in {draft, build, operational, dormant, vacated, closed}` });
    }
    // Health: legacy pipeline-stage rows (negotiating/prospecting) often
    // have no health value because the legacy UI didn't surface the field
    // until a location went operational. The locations table already
    // declares `health TEXT NOT NULL ... DEFAULT 'green'` (see
    // db/schema.sql line 73), so the safe migration behavior is to
    // default to 'green' and warn — not to block the import. A *bad*
    // value (e.g. "turquoise") is still a hard error: that's a contract
    // violation, not a missing-data case.
    let healthForNorm = raw.health;
    if (healthForNorm == null || healthForNorm === "") {
      healthForNorm = "green";
      issues.push({ id, severity: "warning", message: `health defaulted to green during migration` });
    } else if (!VALID_HEALTH.has(healthForNorm)) {
      issues.push({ id, severity: "error", message: `health "${healthForNorm}" not in {green, yellow, red}` });
    }

    // Inline photo warning — bulk-import accepts image_url as a URL, not
    // a base64 payload. We strip it during normalize and warn so the
    // admin knows the photo migration script needs to run separately.
    if (typeof raw.imageUrl === "string" && raw.imageUrl.startsWith("data:")) {
      counts.photosInline += 1;
      issues.push({
        id,
        severity: "warning",
        message: "imageUrl is a base64 payload; will be stripped on import. Upload photos via POST /api/locations/:id/photo instead.",
      });
    }

    // Stub-lease skip. The legacy app left ~19 lease records with
    // empty startDate AND empty endDate (no real lease data, mostly
    // serviced offices and unentered second-market direct leases).
    // leases.start_date / end_date are NOT NULL in the new schema,
    // and we'd rather lose 19 stub records than block the entire
    // 43-location migration. Skip pre-normalize, warn loudly so the
    // admin can recreate via the UI when dates are known.
    //
    // Defensive: only skip when BOTH dates are empty. A half-populated
    // lease (start present, end missing or vice versa) is left in so
    // the server NOT NULL violation surfaces — that pattern likely
    // means real legacy data loss, not a stub record.
    const isEmptyDate = (d) => d == null || d === "";
    const leasesAfterSkip = (raw.leases || []).filter((lease) => {
      if (isEmptyDate(lease?.startDate) && isEmptyDate(lease?.endDate)) {
        counts.leasesSkipped += 1;
        issues.push({
          id,
          severity: "warning",
          message: `lease ${lease?.id ?? "<no id>"} skipped: no startDate or endDate. Recreate via the UI when dates are known.`,
        });
        return false;
      }
      return true;
    });

    // Lease type remap. The legacy app's lease.type enum included
    // "Full Service"; the new schema split that into type ∈
    // {Direct Lease, Sublease, Serviced Office} and a separate
    // opex_structure ∈ {NNN, Modified Gross, Full Service Gross, Gross}.
    // A literal "Full Service" trips leases_type_check and 400s the
    // whole bulk-import. Remap before normalize and warn so the admin
    // sees what we changed. If the source already supplied an
    // opexStructure (future re-export), preserve it.
    const leasesForNorm = leasesAfterSkip.map((lease) => {
      if (lease?.type !== "Full Service") return lease;
      const hasOpex = lease.opexStructure != null && lease.opexStructure !== "";
      issues.push({
        id,
        severity: "warning",
        message: hasOpex
          ? `lease ${lease.id}: type "Full Service" remapped to "Direct Lease"; existing opexStructure "${lease.opexStructure}" preserved`
          : `lease ${lease.id}: type "Full Service" remapped to type="Direct Lease" + opexStructure="Full Service Gross" (legacy enum split across two schema fields)`,
      });
      return hasOpex
        ? { ...lease, type: "Direct Lease" }
        : { ...lease, type: "Direct Lease", opexStructure: "Full Service Gross" };
    });

    // Normalize for schema. This is what we'd POST to /api/bulk-import.
    // healthForNorm carries the defaulted value when the source row was
    // missing one, so the normalized payload matches what the warning
    // told the admin we'd insert. leasesForNorm carries the lease
    // type/opex remap for the same reason.
    let norm;
    try {
      norm = normalizeForSchema({ ...raw, health: healthForNorm, leases: leasesForNorm });
      // image_url MUST be a wire URL or null — never base64 — or the
      // server will accept it as-is and the column will balloon. Strip
      // here; the photo-migration script is the dedicated path.
      if (norm.image_url && String(norm.image_url).startsWith("data:")) {
        norm.image_url = null;
      }
    } catch (err) {
      issues.push({ id, severity: "error", message: `normalizeForSchema threw: ${err.message}` });
      continue;
    }

    // Walk the normalized tree to populate counts. Doing it here, not
    // inside normalizeForSchema, keeps the persistence module pure.
    // normalizeForSchema always returns [] (never null/undefined) for every
    // list field, so the || [] fallbacks below are unnecessary — removed to
    // eliminate permanently-uncoverable branches.
    counts.locations += 1;
    locationIds.push(id);
    for (const lease of norm.leases) {
      counts.leases += 1;
      for (const a of lease.allowances) {
        counts.allowances += 1;
        counts.draws += a.draws.length;
      }
      counts.criticalDates += lease.critical_dates.length;
      counts.watchItems    += lease.watch_items.length;
    }
    counts.projects   += norm.projects.length;
    counts.candidates += norm.candidates.length;
    counts.commentary += norm.commentary.length;
    // Drop utilization rows the server will silently skip (period_start
    // null) so the count reflects what actually lands.
    counts.utilization += norm.utilization.filter((u) => u.period_start).length;
    if (norm.headcount_snapshot) counts.headcount += 1;
    if (norm.location_costs)     counts.locationCosts += 1;

    normalized.push(norm);
  }

  // Errors block; warnings don't. The Validate button wires this through
  // to disable Run-migration when ok=false.
  const hasError = issues.some((i) => i.severity === "error");
  return {
    ok: !hasError && normalized.length > 0,
    issues,
    normalized,
    counts,
    locationIds,
  };
}
